// src/tenantAdmin/pages/TenantCreateUserModal.jsx
// "Crea utente" modal (Brian UI). Self-contained overlay/card (AdminModal's fixed
// title chrome + palette fight this design). Escape closes it only while it is
// the top-most modal (components/ui/modalStack.js).
//
// ⭐ THE DIALOG INVITES — it does not hand out credentials. POST /tenant-admin/users
// takes {email, role} and nothing else (extra="forbid"), so the credential field
// this form used to carry made EVERY submit a 422. Setting one directly lives in
// the edit dialog, on its own route.
//
// THREE OUTCOMES, and only the server can tell them apart. The caller gets one
// object and words it; ⛔ this file never renders the server's `warning` and never
// invents a row:
//   • 'created'  — a brand-new account (inactive) plus a link to choose a
//                  credential. `emailSent` false = the row exists, nobody was
//                  told. A NOME typed here is persisted best-effort with a
//                  follow-up profile PATCH; a failure sets `nameNotSaved`.
//   • 'attached' — 201 from /users/attach: the person already exists and was
//                  added at once (they see the workspace at their next sign-in).
//   • 'invited'  — 202 from /users/attach: nothing changed yet; the person joins
//                  only by accepting the emailed link.
// ⛔ An address that already exists never has its name patched: it is not ours to
// rewrite.
//
// ⛔ `tenantId` is REQUIRED: every tenantAdminApi wrapper names its workspace in
// the query and throws a TypeError without one. The dialog never falls back to
// the active workspace — that is precisely the mistake the argument exists to
// make impossible.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus } from 'lucide-react';
import { createTenantUser, attachTenantUser, updateTenantUserProfile } from '../tenantAdminApi.js';
import { userActionErrorCopy } from '../userActionError.js';
import { COLORS, SEMANTIC, RADII, FONT_SANS, SHADOW } from '../../constants/theme.js';
import { useContentInset } from '../../components/ui/modalArea.js';
import { useModalLayer } from '../../components/ui/modalStack.js';

const fieldLabel = {
  display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px',
  textTransform: 'uppercase', color: COLORS.textSecondary, fontFamily: FONT_SANS,
  marginBottom: '8px',
};
const field = {
  width: '100%', height: '46px', boxSizing: 'border-box',
  background: COLORS.field, border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.control, color: COLORS.ink, fontSize: '14px',
  fontFamily: FONT_SANS, outline: 'none', padding: '0 14px',
};

export default function TenantCreateUserModal({ open, tenantId, onClose, onCreated, tenantName }) {
  // Centred in the page's content area (beside the sidebar), like every app modal — components/ui/modalArea.js.
  const insetLeft = useContentInset(open);
  const isTop = useModalLayer(open);
  const { t } = useTranslation('admin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape' && isTop()) handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function reset() {
    setName('');
    setEmail('');
    setRole('editor');
    setError('');
  }

  // ⛔ OUR copy, keyed on err.code — never `err.message`: buildApiError always
  // sets one and it is server-authored English API prose. An unmapped code is
  // shown AS the code, a handle for support.
  function refusalCopy(err) {
    switch (err.code) {
      case 'USER_SEAT_LIMIT_REACHED':
        // ⛔ The seat rule sentence is deliberately NOT appended: it is wrong on
        // the current backend and is rewritten by the seat cleanup.
        return t('seats.limitReached');
      case 'RATE_LIMITED': {
        // Shared caps across invite and attach, so ONE sentence must fit both.
        // ⛔ Never auto-retry.
        const secs = err.retry_after_seconds;
        return typeof secs === 'number'
          ? t('tenantUsers.rateLimited', { seconds: secs })
          : t('tenantUsers.rateLimitedGeneric');
      }
      case 'USER_TENANT_ASSIGNMENT_EXISTS':
        return t('tenantUsers.alreadyMember');
      case 'AUTH_USER_DEACTIVATED':
        // Since BE 7c5dc05 (2026-09-27): switched off BY AN ADMINISTRATOR, only.
        return t('tenantUsers.accountInactive');
      case 'AUTH_USER_PENDING_ONBOARDING':
        // ⭐ Invited somewhere, never set a password — NOT deactivated. The remedy
        // is time (they sign in) or a resend from where they show as Invitato.
        return t('tenantUsers.pendingOnboarding');
      case 'CANNOT_ATTACH_SUPERADMIN':
        return t('tenantUsers.cannotInviteSuperadmin');
      case 'USER_NOT_FOUND':
        return t('tenantUsers.userNotFound');
      case 'INVALID_ROLE':
        return t('tenantUsers.invalidRole');
      default:
        // The shared fallback: the code as a handle, or — on a 5xx — the
        // request_id to quote (userActionError.js).
        return userActionErrorCopy(t, err);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const nextName = name.trim();
      let outcome;
      try {
        const created = await createTenantUser(tenantId, { email, role });
        // Name isn't part of the create contract — set it best-effort on the new
        // account. A failure here must not fail the invitation (the row exists).
        let nameNotSaved = false;
        if (created && created.user_id && nextName) {
          try {
            await updateTenantUserProfile(tenantId, created.user_id, { first_name: nextName });
          } catch {
            nameNotSaved = true;
          }
        }
        outcome = {
          kind: 'created',
          email,
          emailSent: created?.email_sent === true,
          nameNotSaved,
        };
      } catch (err) {
        // 409 AUTH_EMAIL_EXISTS is what the BE emits on an email collision (a
        // FLAT envelope, `error_code` at the top level, so buildApiError hands it
        // back as err.code). It is decided BEFORE the seat gate, so this fallback
        // runs even when the workspace is full.
        // ⛔ No `!err.code` fallback: a codeless 409 is not a collision.
        if (err.status === 409 && err.code === 'AUTH_EMAIL_EXISTS') {
          const attached = await attachTenantUser(tenantId, { email, role });
          // ⭐ Only `status` separates "in the workspace now" (201) from "asked,
          // nothing changed yet" (202). Anything else is read as the weaker of
          // the two: claiming a membership that does not exist is the worse lie.
          // ⛔ Never patch an existing account's name.
          outcome = attached?.status === 'attached'
            ? { kind: 'attached', email }
            : { kind: 'invited', email, emailSent: attached?.email_sent === true };
        } else {
          throw err;
        }
      }
      reset();
      onCreated(outcome);
      onClose();
    } catch (err) {
      setError(refusalCopy(err));
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    reset();
    onClose();
  }

  if (!open) return null;

  const outlineBtn = {
    height: '46px', padding: '0 22px', borderRadius: RADII.control,
    border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
    color: COLORS.ink, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    fontFamily: FONT_SANS,
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(35,0,62,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
        fontFamily: FONT_SANS,
        ...(insetLeft ? { paddingLeft: `${insetLeft + 24}px` } : null),
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: COLORS.surface, borderRadius: RADII.section, boxShadow: SHADOW.card,
          border: `1px solid ${COLORS.border}`, width: '100%', maxWidth: '560px',
          maxHeight: '90vh', overflow: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '24px 24px 0' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: RADII.pill, flexShrink: 0,
            border: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <UserPlus size={20} color={COLORS.ink} strokeWidth={1.8} />
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, lineHeight: '24px', color: COLORS.ink }}>{t('users.create')}</div>
            <div style={{ fontSize: '12px', color: COLORS.textTertiary }}>
              {t('tenantUsers.createSubtitle', { name: tenantName || '' })}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="tcum-name" style={fieldLabel}>{t('profile.firstName')}</label>
              <input id="tcum-name" type="text" value={name} maxLength={100} onChange={e => setName(e.target.value)} style={field} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="tcum-email" style={fieldLabel}>{t('shared.email')}</label>
              <input id="tcum-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder={t('tenantUsers.emailPlaceholder')} style={field} />
              <span style={{ fontSize: '11px', color: COLORS.textTertiary, marginTop: '6px', display: 'block' }}>
                {t('tenantUsers.inviteHelp')}
              </span>
            </div>

            <div>
              <label htmlFor="tcum-role" style={fieldLabel}>{t('shared.role')}</label>
              <select id="tcum-role" value={role} onChange={e => setRole(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {error && (
              <div style={{
                marginTop: '20px', padding: '10px 14px', borderRadius: RADII.control,
                background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}33`,
                color: SEMANTIC.errore.fg, fontSize: '13px',
              }}>
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: '12px',
            padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          }}>
            <button type="button" onClick={handleClose} style={outlineBtn}>
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                height: '46px', padding: '0 22px', borderRadius: RADII.control, border: 'none',
                background: loading ? COLORS.borderStrong : COLORS.accent, color: COLORS.surface,
                fontSize: '14px', fontWeight: 600, fontFamily: FONT_SANS,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? t('users.creating') : t('tenantUsers.createUser')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
